import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  subscribeToPush,
  savePushSubscription,
  unsubscribeFromPush,
  isPushSupported,
  getCurrentSubscription,
  removePushSubscription
} from "@/lib/pushNotifications";

export interface NotificationPreferences {
  dailyNotificationEnabled: boolean;
  dailyNotificationTime: string;
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    dailyNotificationEnabled: false,
    dailyNotificationTime: "08:00",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Check if push subscription exists
  const checkPushSubscriptionExists = async (): Promise<boolean> => {
    try {
      const subscription = await getCurrentSubscription();
      return !!subscription;
    } catch (error) {
      console.error("Error checking push subscription:", error);
      return false;
    }
  };

  // Load preferences from database and check push subscription
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        // Load user preferences from database
        const { data, error } = await supabase
          .from("user_preferences")
          .select("daily_notification_enabled, daily_notification_time")
          .eq("user_id", user.id)
          .single();

        if (error && error.code !== "PGRST116") { // PGRST116 is "not found"
          console.error("Error loading notification preferences:", error);
          toast.error("Failed to load notification preferences");
          return;
        }

        // Check if push subscription exists
        const hasPushSubscription = await checkPushSubscriptionExists();

        // Set preferences based on database and push subscription status
        if (data) {
          setPreferences({
            dailyNotificationEnabled: hasPushSubscription && (data.daily_notification_enabled ?? false),
            dailyNotificationTime: data.daily_notification_time
              ? data.daily_notification_time.substring(0, 5) // Take HH:MM part
              : "08:00",
          });
        } else {
          // No database preferences, use push subscription status
          setPreferences({
            dailyNotificationEnabled: hasPushSubscription,
            dailyNotificationTime: "08:00",
          });
        }
      } catch (error) {
        console.error("Error loading notification preferences:", error);
        toast.error("Failed to load notification preferences");
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [user?.id]);

  // Save preferences to database
  const savePreferences = async (newPreferences: NotificationPreferences) => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      // Convert time to HH:MM:SS format for database
      const timeForDb = `${newPreferences.dailyNotificationTime}:00`;

      const { error } = await supabase
        .from("user_preferences")
        .upsert({
          user_id: user.id,
          daily_notification_enabled: newPreferences.dailyNotificationEnabled,
          daily_notification_time: timeForDb,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "user_id"
        });

      if (error) throw error;

      setPreferences(newPreferences);
      toast.success("Notification preferences saved");

      // Handle push subscriptions for background notifications
      if (newPreferences.dailyNotificationEnabled) {
        console.log('Notifications enabled, ensuring push subscription...');

        // Check if we already have a valid subscription
        const currentSubscription = await getCurrentSubscription();
        if (!currentSubscription) {
          // Subscribe to push notifications for background delivery
          if (isPushSupported()) {
            try {
              console.log('Creating new push subscription...');
              const subscriptionData = await subscribeToPush();
              console.log('Push subscription data:', subscriptionData);

              if (subscriptionData) {
                console.log('Saving push subscription to backend...');
                await savePushSubscription(subscriptionData);
                console.log('Push subscription saved successfully');
                toast.success('Push notifications enabled for background delivery');
              } else {
                console.warn('No subscription data received');
                toast.info('Using foreground notifications (app must be open)');
              }
            } catch (error) {
              console.error('Push subscription failed:', error);
              toast.error(`Push notifications failed: ${error.message}`);
              toast.info('Using foreground notifications (app must be open)');
            }
          } else {
            console.log('Push notifications not supported on this device');
            toast.info('Push notifications not supported - using foreground notifications');
          }
        } else {
          console.log('Push subscription already exists');
          toast.success('Push notifications enabled (existing subscription found)');
        }

        // Schedule local notifications for when app is open
        await scheduleDailyNotification(newPreferences.dailyNotificationTime);
      } else {
        console.log('Notifications disabled, cleaning up...');
        // Cancel notifications and unsubscribe from push
        await cancelDailyNotification();
        try {
          await unsubscribeFromPush();
          await removePushSubscription();
          console.log('Push subscription unsubscribed and removed');
        } catch (error) {
          console.warn('Error unsubscribing from push:', error);
        }
      }
    } catch (error) {
      console.error("Error saving notification preferences:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setIsSaving(false);
    }
  };

  // Schedule daily notification using service worker for background support
  const scheduleDailyNotification = async (time: string) => {
    if (!("Notification" in window)) {
      console.warn("Notification API not supported");
      return;
    }

    try {
      // Request notification permission if not granted
      if (Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          toast.error("Notification permission is required for daily reminders");
          return;
        }
      }

      console.log(`Scheduling background notification for ${time}`);

      // Try service worker controller first
      if (navigator.serviceWorker.controller) {
        try {
          navigator.serviceWorker.controller.postMessage({
            type: "SCHEDULE_DAILY_NOTIFICATION",
            time: time,
          });
          console.log("Notification scheduled via service worker controller");
          return;
        } catch (swError) {
          console.warn("Service worker controller message failed, using fallback:", swError);
        }
      } else {
        console.log("No service worker controller available");
      }

      // Fallback: schedule using browser API (only works when page is open)
      console.log("Using browser API fallback for scheduling");
      const [hours, minutes] = time.split(':').map(Number);
      const now = new Date();
      const targetTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);

      if (targetTime <= now) {
        targetTime.setDate(targetTime.getDate() + 1);
      }

      const timeUntilNotification = targetTime.getTime() - now.getTime();

      setTimeout(() => {
        if (Notification.permission === "granted") {
          new Notification("Daily Habit Reminder 🗓️", {
            body: "Time to log your habits and maintain your streaks!",
            icon: "/logo.png",
            silent: false,
          });
          // Schedule next one
          scheduleDailyNotification(time);
        }
      }, timeUntilNotification);

      console.log("Notification scheduled via browser API fallback");

    } catch (error) {
      console.error("Error scheduling daily notification:", error);
      toast.error("Failed to schedule daily notification");
    }
  };

  // Cancel daily notification
  const cancelDailyNotification = async () => {
    if (navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: "CANCEL_DAILY_NOTIFICATION",
        });
        console.log("Cancel notification sent to SW controller");
      } catch (error) {
        console.error("Error canceling daily notification:", error);
      }
    }
  };

  return {
    preferences,
    isLoading,
    isSaving,
    savePreferences,
  };
}
